import type { Locale } from "@chams-pay/contracts";
import { formatDisplayDate, formatWholeFigure, translate } from "@chams-pay/contracts";
import { cn } from "@chams-pay/design-system/lib/utils";
import { Bus, GraduationCap, HandHelping, type LucideIcon } from "lucide-react";
import type { StudentServiceCode } from "../core/format.js";
import { studentServices } from "../core/format.js";

/**
 * Every mixed-direction value (references, dates, digits, percentages) is
 * isolated so an Arabic paragraph never reorders a Latin figure. Figures always
 * carry `.chams-figure` — Cairo, tabular numerals.
 */
export function Bidi({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <bdi dir="ltr" className={cn("chams-figure", className)}>
      {children}
    </bdi>
  );
}

export function Amount({
  value,
  locale,
  className,
  signed = false,
  tone = "default",
}: {
  readonly value: string | bigint;
  readonly locale: Locale;
  readonly className?: string;
  /** Renders the figure as a deduction with a true minus sign. */
  readonly signed?: boolean;
  readonly tone?: "default" | "destructive" | "primary" | "muted";
}) {
  const amount = typeof value === "bigint" ? value : BigInt(value);
  const negative = signed && amount !== 0n;
  return (
    <span
      dir={locale === "ar" ? "rtl" : "ltr"}
      className={cn(
        "chams-figure inline-flex items-baseline gap-1 whitespace-nowrap",
        tone === "destructive" && "text-destructive",
        tone === "primary" && "text-primary",
        tone === "muted" && "text-muted-foreground",
        className,
      )}
    >
      <bdi dir="ltr">
        {negative ? "−" : ""}
        {formatWholeFigure(amount < 0n ? -amount : amount)}
      </bdi>
      <span>{locale === "ar" ? "درهم" : "MAD"}</span>
    </span>
  );
}

/** Percentages are figures too: Cairo, tabular, LTR-isolated. */
export function Percentage({
  value,
  className,
  unitClassName,
}: {
  readonly value: string | number;
  readonly className?: string;
  readonly unitClassName?: string;
}) {
  const numeric = typeof value === "number" ? value : Number(value);
  const rounded = Number.isFinite(numeric) ? Math.round(numeric) : 0;
  return (
    <bdi dir="ltr" className={cn("chams-figure whitespace-nowrap", className)}>
      {String(rounded)}
      <span className={cn("font-semibold", unitClassName)}>%</span>
    </bdi>
  );
}

export function DateValue({
  value,
  className,
}: {
  readonly value: string | null;
  readonly className?: string;
}) {
  if (!value) return <UnavailableFigure {...(className ? { className } : {})} />;
  return (
    <bdi dir="ltr" className={cn("chams-figure", className)}>
      {formatDisplayDate(value)}
    </bdi>
  );
}

export function Count({
  value,
  className,
}: {
  readonly value: number;
  readonly className?: string;
}) {
  return (
    <bdi dir="ltr" className={cn("chams-figure", className)}>
      {String(value)}
    </bdi>
  );
}

/** The dashed placeholder that keeps a figure's geometry when it is unavailable. */
export function UnavailableFigure({ className }: { readonly className?: string }) {
  return (
    <bdi dir="ltr" className={cn("chams-figure text-disabled", className)} aria-hidden="true">
      {"——"}
    </bdi>
  );
}

const serviceIcons: Record<
  StudentServiceCode,
  { icon: LucideIcon; labelKey: Parameters<typeof translate>[1] }
> = {
  tuition: { icon: GraduationCap, labelKey: "students.serviceTuition" },
  transport: { icon: Bus, labelKey: "students.serviceTransport" },
  accompaniment: { icon: HandHelping, labelKey: "students.serviceAccompaniment" },
};

export function ServiceIcons({
  locale,
  summaryAr,
  summaryFr,
  className,
}: {
  readonly locale: Locale;
  readonly summaryAr: string;
  readonly summaryFr: string;
  readonly className?: string;
}) {
  const services = studentServices(summaryAr, summaryFr);
  if (!services.length) return null;
  return (
    <span
      className={cn("flex items-center gap-1", className)}
      role="list"
      aria-label={translate(locale, "students.services")}
    >
      {services.map((code) => {
        const service = serviceIcons[code];
        const label = translate(locale, service.labelKey);
        const Glyph = service.icon;
        return (
          <span
            key={code}
            role="listitem"
            title={label}
            aria-label={label}
            data-service={code}
            className="student-service-icon grid size-7 place-items-center rounded-lg bg-primary-soft text-primary-strong"
          >
            <Glyph aria-hidden="true" className="size-4" strokeWidth={1.9} />
          </span>
        );
      })}
    </span>
  );
}
